import React, { Suspense, useRef, useState, useEffect, useMemo } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { PerspectiveCamera, Environment, MeshDistortMaterial, ContactShadows, Text, Billboard, Clouds, Cloud } from '@react-three/drei'
import { useSpring } from '@react-spring/core'
import { a } from '@react-spring/three'
import * as THREE from 'three'
import { LeftOutlined, RightOutlined, PlayCircleOutlined, PauseCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons'; // 添加 ArrowLeftOutlined
import { ConfigProvider, Slider } from 'antd';
import { progressNodeStyle } from '../../Components/ProgressBar';
import ProgressBar from '../../Components/ProgressBar';
import Part3Gallery from '../Part3Gallery/Part3Gallery'; // 添加这一行导入单独的 Part3Gallery 组件

const AnimatedMaterial = a(MeshDistortMaterial)

// 创建文字纹理
function createTextTexture(text) {
    const canvas = document.createElement('canvas')
    canvas.width = 1024
    canvas.height = 1024
    const context = canvas.getContext('2d')

    // 创建一个动画纹理
    const texture = new THREE.CanvasTexture(canvas)
    texture.needsUpdate = true

    // 设置动画更新函数
    const updateTexture = () => {
        // 清除画布
        context.fillStyle = 'white'
        context.fillRect(0, 0, canvas.width, canvas.height)

        // 设置文字样式
        context.fillStyle = 'black'
        context.font = '14px Inter'
        context.textAlign = 'center'
        context.textBaseline = 'middle'

        // 文字自动换行函数
        function wrapText(text, maxWidth) {
            const words = text.split(' ')
            const lines = []
            let currentLine = words[0]

            for (let i = 1; i < words.length; i++) {
                const word = words[i]
                const width = context.measureText(currentLine + " " + word).width
                if (width < maxWidth) {
                    currentLine += " " + word
                } else {
                    lines.push(currentLine)
                    currentLine = word
                }
            }
            lines.push(currentLine)
            return lines
        }

        // 绘制文字
        const lines = wrapText(text, canvas.width * 0.2)
        lines.forEach((line, i) => {
            context.fillText(
                line,
                canvas.width / 4,
                canvas.height / 2 - (lines.length / 2 - i) * 30
            )
        })

        // 添加闪光效果 - 从左上角到右下角的渐变
        const time = Date.now() % 10000 / 10000  // 5秒一个周期
        // 使用余弦函数替代正弦，让动画更平滑
        const gradientPosition = (1 - Math.cos(time * Math.PI * 2)) / 2  // 0到1之间的值，更平滑的过渡

        // 只在特定范围显示闪光，避免起始和结束时的卡顿
        if (gradientPosition > 0.05 && gradientPosition < 0.95) {
            const gradient = context.createLinearGradient(
                0, 0,  // 左上角
                canvas.width, canvas.height  // 右下角
            )

            // 使用线性映射来控制闪光位置，避免突变
            // 将0.05-0.95映射到0.1-0.9范围
            const mappedPos = 0.1 + (gradientPosition - 0.05) * (0.8 / 0.9)

            // 设置渐变颜色，确保所有值都在0-1范围内
            gradient.addColorStop(Math.max(0, mappedPos - 0.1), 'rgba(255, 255, 255, 0)')
            gradient.addColorStop(mappedPos, 'rgba(255, 255, 255, 0.5)')
            gradient.addColorStop(Math.min(1, mappedPos + 0.1), 'rgba(255, 255, 255, 0)')

            // 应用闪光效果
            context.globalCompositeOperation = 'lighter'
            context.fillStyle = gradient
            context.fillRect(0, 0, canvas.width, canvas.height)
            context.globalCompositeOperation = 'source-over'
        }

        texture.needsUpdate = true
        requestAnimationFrame(updateTexture)
    }

    // 开始动画
    updateTexture()

    return texture
}

// 修改 Blob 组件，彻底解决文本不显示问题
// 用HTML直接显示文本，完全跳过Three.js的Text组件，解决延迟问题

function Blob({ currentPotatoData, windyMode }) {
    const sphere = useRef()
    const light = useRef()
    const [hovered, setHovered] = useState(false)
    // 始终使用强引用保存当前文本值
    const [textContent, setTextContent] = useState("Loading...")

    // 使用传入
    const textTexture = useMemo(() => {
        return createTextTexture(
            currentPotatoData?.englishDescription || "Loading..."
        )
    }, [currentPotatoData])

    // 添加随机参数状态
    const [materialParams] = useState(() => ({
        metalness: Math.random(),  
        distort: Math.random(),        
        speed: Math.random() * 10 + 1   
    }));
    
    // 每次 currentPotatoData 改变时立即更新文本内容
    useEffect(() => {
        if (sphere.current) {
            sphere.current.material.metalness = Math.random();
            sphere.current.material.distort = Math.random();
            sphere.current.material.speed = Math.random() * 10 + 1;
        }
        
        // 记录当前数据，帮助调试
        console.log("Current potato data changed:", currentPotatoData);
        
        // 直接从当前数据更新文本，每次数据变化一定会执行
        if (currentPotatoData) {
            // 按优先级尝试不同的文本源
            let text = "未知土豆";
            
            if (currentPotatoData.specialParam) {
                text = currentPotatoData.specialParam;
                console.log("使用了 specialParam:", text);
            } else if (currentPotatoData.specialParamEn) {
                text = currentPotatoData.specialParamEn;
                console.log("使用了 specialParamEn:", text);
            } else if (currentPotatoData.chineseName) {
                text = currentPotatoData.chineseName;
                console.log("使用了 chineseName:", text);
            } else if (currentPotatoData.englishName) {
                text = currentPotatoData.englishName;
                console.log("使用了 englishName:", text);
            } else {
                text = `火星土豆 #${currentPotatoData.id}`;
                console.log("使用了默认名称:", text);
            }
            
            setTextContent(text);
        }
    }, [currentPotatoData]);

    useEffect(() => {
        // 确保组件卸载时取消动画循环
        return () => {
            if (textTexture && textTexture.dispose) {
                textTexture.dispose()
            }
        }
    }, [textTexture])

    useFrame((state) => {
        light.current.position.x = state.mouse.x * 20
        light.current.position.y = state.mouse.y * 20
        if (sphere.current) {
            sphere.current.position.x = THREE.MathUtils.lerp(sphere.current.position.x, hovered ? state.mouse.x / 2 : 0, 0.2)
            sphere.current.position.y = THREE.MathUtils.lerp(
                sphere.current.position.y,
                Math.sin(state.clock.elapsedTime / 1.5) / 6 + (hovered ? state.mouse.y / 2 : 0),
                0.2
            )
        }
    })

    const [{ wobble, color, env }] = useSpring(
        {
            wobble: hovered ? 1.05 : 1,
            env: 1,
            color: '#fff',
            config: { mass: 2, tension: 1000, friction: 10 }
        },
        [hovered]
    )
    
    return (
        <>
            <PerspectiveCamera makeDefault position={[0, 0, 4]} fov={75}>
                <a.pointLight ref={light} position-z={-15} intensity={1.5} color="#fff" />
            </PerspectiveCamera>
            <group>
                <a.mesh
                    ref={sphere}
                    scale={wobble}
                    onPointerOver={() => setHovered(true)}
                    onPointerOut={() => setHovered(false)}>
                    <sphereGeometry args={[1, 64, 64]} />
                    <AnimatedMaterial
                        color={color}
                        envMapIntensity={env}
                        clearcoat={1}
                        clearcoatRoughness={0}
                        metalness={materialParams.metalness}
                        distort={materialParams.distort}
                        speed={materialParams.speed}
                        map={textTexture}
                        transparent
                        opacity={windyMode ? 0 : 0.9}
                    />
                </a.mesh>
                
                {/* 不再使用Three.js的Text组件，改为传递textContent到外部 */}
            </group>
        </>
    )
}

// 添加云朵组件
function CloudsComponent({ windyMode, showWindImage }) {
    const cloudGroupRef = useRef()
    const imgMeshRef = useRef()
    useFrame((state) => {
        if (cloudGroupRef.current) {
            const time = state.clock.elapsedTime
            const windFactor = windyMode ? 3 : 1
            cloudGroupRef.current.position.x = Math.sin(time * 0.5 * windFactor) * (2 * windFactor)
            cloudGroupRef.current.position.y = Math.sin(time * 0.3 * windFactor) * (1.5 * windFactor)
            cloudGroupRef.current.position.z = Math.sin(time * 0.4 * windFactor) * (3 * windFactor)
            
            // 添加旋转效果
            cloudGroupRef.current.rotation.y = Math.sin(time * 0.2 * windFactor) * (0.2 * windFactor)
            cloudGroupRef.current.rotation.z = Math.cos(time * 0.3 * windFactor) * (0.1 * windFactor)
        }
    })
    // Three.js纹理
    const [imgTexture] = useState(() => {
        const tex = new THREE.TextureLoader().load('/imgs/MRI.jpg')
        return tex
    })
    return (
        <group ref={cloudGroupRef}>
            <Clouds texture="/imgs/cloud.png">
                <Cloud 
                    concentrate="outside" 
                    seed={1} 
                    segments={60} 
                    bounds={6}
                    volume={12} 
                    growth={4} 
                    opacity={windyMode ? 0.25 : 0.07} 
                    position={[0, 0, 2]}
                    speed={windyMode ? 2.5 : 0.8}
                />
                <Cloud 
                    concentrate="center" 
                    seed={2} 
                    segments={40} 
                    bounds={4}
                    volume={8} 
                    growth={3} 
                    opacity={windyMode ? 0.09 : 0.017} 
                    position={[0, 0, 0]}
                    speed={windyMode ? 1.8 : 0.6}
                />
                <Cloud 
                    concentrate="inside" 
                    seed={3} 
                    segments={30} 
                    bounds={8}
                    volume={15} 
                    growth={5} 
                    opacity={windyMode ? 0.04 : 0.01} 
                    position={[0, 0, -2]}
                    speed={windyMode ? 1.2 : 0.4}
                />
            </Clouds>
            {/* 风中图片 */}
            {windyMode && showWindImage && (
                <mesh ref={imgMeshRef} position={[0,0,0]}>
                    <planeGeometry args={[6, 4]} />
                    <meshBasicMaterial map={imgTexture} transparent opacity={0.01} />
                </mesh>
            )}
        </group>
    )
}

// 新增Part3Gallery组件

const PotatoPlanet = ({ potatoData }) => {
    const [progressStep, setProgressStep] = useState(() => {
        // 获取已达到的最高进度
        return Number(localStorage.getItem('mars-highest-progress-step')) || 2; // 默认为第2步
    });

    const [currentIndex, setCurrentIndex] = useState(0)
    const [isDarkMode, setIsDarkMode] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [windyMode, setWindyMode] = useState(false)
    const [showWindImage, setShowWindImage] = useState(false)
    const [imgIn, setImgIn] = useState(false)
    const [maskProgress, setMaskProgress] = useState(0)
    const [showPart3, setShowPart3] = useState(false)
    const [confirmHover, setConfirmHover] = useState(false);
    const [backHover, setBackHover] = useState(false); // 添加返回按钮悬停状态
    const [defineHover, setDefineHover] = useState(false); // 添加"认定优秀品种"按钮悬停状态
    const [potatoFeatureText, setPotatoFeatureText] = useState("Loading...");

    useEffect(() => {
        if (potatoData && potatoData[currentIndex]) {
            console.log('---------------------------');
            console.log(`当前索引: ${currentIndex} / ${potatoData.length - 1}`);
            console.log('当前土豆数据:', potatoData[currentIndex]);
            console.log('特殊参数:', potatoData[currentIndex].specialParam);
            console.log('---------------------------');
            
            // 直接更新功能文本状态
            const data = potatoData[currentIndex];
            let text = "未知土豆";
            
            if (data.specialParam) {
                text = data.specialParam;
            } else if (data.specialParamEn) {
                text = data.specialParamEn;
            } else if (data.chineseName) {
                text = data.chineseName;
            } else if (data.englishName) {
                text = data.englishName;
            } else {
                text = `火星土豆 #${data.id}`;
            }
            
            setPotatoFeatureText(text);
        }
    }, [currentIndex, potatoData]);
    
    // 激活 windyMode 后延迟显示图片
    useEffect(() => {
        let timer
        if (windyMode) {
            timer = setTimeout(() => setShowWindImage(true), 600)
        } else {
            setShowWindImage(false)
            setImgIn(false)
            setMaskProgress(0)
        }
        return () => clearTimeout(timer)
    }, [windyMode])
    // 图片渲染后下一帧触发动画
    useEffect(() => {
        if (showWindImage) {
            setImgIn(false)
            setMaskProgress(0)
            const raf = requestAnimationFrame(() => setImgIn(true))
            return () => cancelAnimationFrame(raf)
        }
    }, [showWindImage])
    // mask动画
    useEffect(() => {
        if (!imgIn) return
        let start = null
        let frame
        const duration = 1200 // ms
        function animateMask(ts) {
            if (!start) start = ts
            const elapsed = ts - start
            let progress = Math.min(elapsed / duration, 1)
            setMaskProgress(progress)
            if (progress < 1) {
                frame = requestAnimationFrame(animateMask)
            }
        }
        frame = requestAnimationFrame(animateMask)
        return () => cancelAnimationFrame(frame)
    }, [imgIn])

    useEffect(() => {
        let timer;
        if (isPlaying && potatoData.length > 0) {
            timer = setInterval(() => {
                setCurrentIndex(prev => {
                    if (prev >= potatoData.length - 1) {
                        setIsPlaying(false); // 播放完成后停止
                        return 0;
                    }
                    return prev + 1;
                });
            }, 5000); // 3秒切换一次
        }
        return () => clearInterval(timer);
    }, [isPlaying, potatoData.length]);

    const arrowStyle = {
        position: 'fixed',
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: '24px',
        color: isDarkMode ? 'white' : 'black',
        cursor: 'pointer',
        zIndex: 1000,
        padding: '20px 10px',
    };

    const sliderContainerStyle = {
        position: 'fixed',
        bottom: '40px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '80%',
        maxWidth: '800px',
        padding: '20px',
        zIndex: 1000
    };

    const playButtonStyle = {
        position: 'fixed',
        top: '20px',
        right: '20px',
        fontSize: '32px',
        color: isDarkMode ? 'white' : 'black',
        cursor: 'pointer',
        zIndex: 1000,
    };

    const sliderTokens = {
        handleSize: 16,
        handleSizeHover: 20,
        // handleColor: isDarkMode ? '#177ddc' : '#CC580A',
         handleColor: '#000000',
        handleActiveColor: isDarkMode ? '#40a9ff' : '#FF8B4D',
        handleActiveOutlineColor: isDarkMode ? 'rgba(64,169,255,0.2)' : 'rgba(255,114,44,0.2)', // 圆环秒变颜色
        railBg: '#DA6C36', // 更亮的橙色
        railHoverBg: '#FFD2B2',
        trackBg: isDarkMode ? '#177ddc80' : '#CC580A',
        trackHoverBg: isDarkMode ? '#40a9ff80' : '#FF8B4D',
        dotSize: 8,
        railSize: 4,
        // controlSize: 10,
        // handleLineWidth: 2,
        // handleLineWidthHover: 2.5
    };

    // 放大版节点样式
    const bigNodeStyle = {
        ...progressNodeStyle,
        width: 64,
        height: 64,
        fontSize: 22,
        border: '2.5px solid #fff',
    };

    // 确认按钮点击
// 修改确认按钮点击处理函数
function handleConfirm() {
    // 获取当前最高进度
    const currentHighest = Number(localStorage.getItem('mars-highest-progress-step')) || 2;
    // 如果第3步更高，则更新最高进度
    if (3 > currentHighest) {
        localStorage.setItem('mars-highest-progress-step', '3');
    }
    // 保存当前进度
    localStorage.setItem('mars-progress-step', '3');
    
    // 更新状态
    setProgressStep(3);
    // 显示第3部分
    setShowPart3(true);
}

    // 返回按钮的点击处理函数
    const handleBackClick = () => {
        setWindyMode(false);
        setShowWindImage(false);
        setImgIn(false);
        setMaskProgress(0);
    };

    // 如果没有数据，显示加载状态
    if (!potatoData || potatoData.length === 0) {
        return (
            <div style={{ width: '100vw', height: '100vh', background: '#FF722C', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="bg-black/70 text-white/90 px-4 py-2 rounded-lg backdrop-blur-sm">
                    正在加载土豆数据...
                </div>
            </div>
        );
    }

    // 页面切换逻辑
    if (showPart3) return <Part3Gallery />;

    return (
        <div style={{ width: '100vw', height: '100vh', background: '#FF722C', position: 'relative', overflow: 'hidden' }}>
            {/* 使用正确的prop名称 */}
            <ProgressBar currentStep={2} />
            
            {/* 播放按钮 */}
            <div 
                style={{
                    ...playButtonStyle,
                    opacity: windyMode ? 0 : 1,
                    pointerEvents: windyMode ? 'none' : 'auto',
                    transition: 'opacity 0.6s',
                }}
                onClick={() => setIsPlaying(!isPlaying)}
            >
                {isPlaying ? 
                    <PauseCircleOutlined /> : 
                    <PlayCircleOutlined />
                }
            </div>

                        {!windyMode && (
                <div 
                    style={{
                        position: 'fixed',
                        bottom: '260px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: '#000',
                        fontWeight: 'regular',
                        fontSize: '16px',
                        textAlign: 'center',
                        padding: '8px 15px',
                        // borderRadius: '50px',
                        // background: 'rgba(255,255,255,0.4)',
                        // backdropFilter: 'blur(5px)',
                        // boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        zIndex: 3000,
                        minWidth: '200px',
                        maxWidth: '80%',
                        pointerEvents: 'none',
                        transition: 'opacity 0.3s',
                    }}
                >
                    {potatoFeatureText}
                </div>
            )}

            {/* 添加返回按钮 - 只在 windyMode 和 showWindImage 为 true 时显示 */}
            {windyMode && showWindImage && !showPart3 && (
                <button
                    style={{
                        position: 'fixed',
                        top: '20px',
                        left: '20px',
                        zIndex: 4000,
                        width: 50,
                        height: 50,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.15)',
                        border: `1.5px solid ${backHover ? '#fff' : '#111'}`,
                        color: backHover ? '#fff' : '#111',
                        fontSize: 18,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 32px 0 rgba(0,0,0,0.10)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'color 0.2s, border 0.2s',
                    }}
                    onClick={handleBackClick}
                    onMouseEnter={() => setBackHover(true)}
                    onMouseLeave={() => setBackHover(false)}
                >
                    <ArrowLeftOutlined />
                </button>
            )}

            {/* 左箭头 */}
            {currentIndex > 0 && (
                <LeftOutlined 
                    style={{ ...arrowStyle, left: '20px', opacity: windyMode ? 0 : 1, pointerEvents: windyMode ? 'none' : 'auto', transition: 'opacity 0.6s' }}
                    onClick={() => {
                        setIsPlaying(false); // 点击箭头时停止自动播放
                        setCurrentIndex(prev => Math.max(0, prev - 1));
                    }}
                />
            )}

            {/* 右箭头 */}
            {currentIndex < potatoData.length - 1 && (
                <RightOutlined 
                    style={{ ...arrowStyle, right: '20px', opacity: windyMode ? 0 : 1, pointerEvents: windyMode ? 'none' : 'auto', transition: 'opacity 0.6s' }}
                    onClick={() => {
                        setIsPlaying(false); // 点击箭头时停止自动播放
                        setCurrentIndex(prev => Math.min(potatoData.length - 1, prev + 1));
                    }}
                />
            )}

            {/* 滑动条 */}
            <div style={{ ...sliderContainerStyle, opacity: windyMode ? 0 : 1, pointerEvents: windyMode ? 'none' : 'auto', transition: 'opacity 0.6s' }}>
                <ConfigProvider
                    theme={{
                        components: {
                            Slider: sliderTokens
                        },
                    }}
                >
                    <Slider
                        min={0}
                        max={potatoData.length - 1}
                        value={currentIndex}
                        onChange={(value) => {
                            setIsPlaying(false); // 拖动滑块时停止自动播放
                            setCurrentIndex(value);
                        }}
                        tooltip={{
                            formatter: val => `${val + 1}/${potatoData.length}`
                        }}
                    />
                </ConfigProvider>
            </div>

            {/* 认定优秀品种按钮 */}
            <button
                style={{
                    position: 'fixed',
                    bottom: '40px',
                    right: '40px',
                    zIndex: 2000,
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: defineHover ? '#fff' : '#111',
                    background: 'rgba(255,255,255,0.15)',
                    border: `1.5px solid ${defineHover ? '#fff' : '#111'}`,
                    borderRadius: '50rem',
                    padding: '0.8rem 2rem',
                    boxShadow: '0 4px 32px 0 rgba(0,0,0,0.10)',
                    cursor: 'pointer',
                    opacity: windyMode ? 0 : 1,
                    pointerEvents: windyMode ? 'none' : 'auto',
                    transition: 'color 0.2s, border 0.2s, opacity 0.6s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    userSelect: 'none',
                }}
                onClick={() => setWindyMode(true)}
                onMouseEnter={() => setDefineHover(true)}
                onMouseLeave={() => setDefineHover(false)}
            >认定优秀品种</button>
            
            {/* Canvas始终渲染云朵，MRI图片飘入时只隐藏Blob和其他内容 */}
            <Canvas gl={{ antialias: true }} style={{ pointerEvents: 'none' }}>
                <Suspense fallback={null}>
                    <CloudsComponent windyMode={windyMode} showWindImage={false} />
                    {/* 只有非windyMode时渲染Blob和其他内容 */}
                    {!windyMode && (
                        <>
                            <Blob currentPotatoData={potatoData[currentIndex]} windyMode={windyMode} />
                            <Environment files="hdr-beautiful-scenery-beach-with-sunset-clouds.jpg" blurriness={15} />
                            <ContactShadows
                                rotation={[Math.PI / 2, 0, 0]}
                                position={[0, -1.7, 0]}
                                opacity={0.8}
                                width={15}
                                height={15}
                                blur={2.5}
                                far={1.6}
                            />
                        </>
                    )}
                </Suspense>
            </Canvas>
            
            {/* MRI图片全屏飘入，z-index高于Canvas，云朵始终可见 */}
            {windyMode && showWindImage && !showPart3 && (
                <img
                    src="/imgs/MRI.jpg"
                    alt="MRI"
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        objectFit: 'cover',
                        zIndex: 3000,
                        opacity: 0.92,
                        pointerEvents: 'none',
                        transition: 'transform 1.2s cubic-bezier(0.77,0,0.175,1)',
                        transform: imgIn ? 'translateX(0)' : 'translateX(-100vw)',
                        WebkitMaskImage: `linear-gradient(to right, black ${(maskProgress*100).toFixed(1)}%, transparent 100%)`,
                        maskImage: `linear-gradient(to right, black ${(maskProgress*100).toFixed(1)}%, transparent 100%)`,
                    }}
                    className="mri-fadein"
                />
            )}
            
            {/* MRI图片飘入后右下角确认按钮 */}
            {windyMode && showWindImage && !showPart3 && (
                <button
                    style={{
                        position: 'fixed',
                        bottom: '40px',
                        right: '40px',
                        zIndex: 4000,
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.15)',
                        border: `1.5px solid ${confirmHover ? '#fff' : '#111'}`,
                        color: confirmHover ? '#fff' : '#111',
                        fontSize: 22,
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 32px 0 rgba(0,0,0,0.10)',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'color 0.2s, border 0.2s',
                    }}
                    onClick={handleConfirm}
                    onMouseEnter={() => setConfirmHover(true)}
                    onMouseLeave={() => setConfirmHover(false)}
                >确认</button>
            )}
        </div>
    );
};

export default PotatoPlanet;